import { useRef, useState, useCallback, useEffect } from 'react';
import { Camera, FileImage, ChevronDown, X, SwitchCamera, Plus } from 'lucide-react';
import { Button } from '~/components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '~/components/ui/dialog';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '~/components/ui/dropdown-menu';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '~/components/ui/select';

type MedicalImageCaptureProps = {
    onImageCaptured: (file: File) => void;
    disabled?: boolean;
    compact?: boolean;
    /** Skip the "Übernehmen" confirmation -- capture goes directly to onImageCaptured */
    autoCapture?: boolean;
    /** Automatically open the camera when mounted / when this transitions to true */
    autoOpen?: boolean;
    /** Called when the camera dialog is closed without capturing (only relevant with autoOpen) */
    onCancelled?: () => void;
};

const isBackCamera = (device: MediaDeviceInfo): boolean => {
    const label = device.label.toLowerCase();
    return label.includes('back') || label.includes('rear') || label.includes('environment') || label.includes('rück');
};

const MedicalImageCapture = ({ onImageCaptured, disabled, compact, autoCapture, autoOpen, onCancelled }: MedicalImageCaptureProps) => {
    const fileInputRef = useRef<HTMLInputElement>(null);
    const videoRef = useRef<HTMLVideoElement>(null);
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const autoCaptureRef = useRef(autoCapture);
    autoCaptureRef.current = autoCapture;

    const [cameraDialogOpen, setCameraDialogOpen] = useState(false);
    const [cameraStream, setCameraStream] = useState<MediaStream | null>(null);
    const [capturedImage, setCapturedImage] = useState<string | null>(null);
    const [capturedFile, setCapturedFile] = useState<File | null>(null);
    const [cameraError, setCameraError] = useState<string | null>(null);
    const [availableCameras, setAvailableCameras] = useState<MediaDeviceInfo[]>([]);
    const [selectedCameraId, setSelectedCameraId] = useState<string | null>(null);

    const handleFileSelect = () => {
        fileInputRef.current?.click();
    };

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            onImageCaptured(file);
        }
        e.target.value = '';
    };

    const loadAvailableCameras = async () => {
        try {
            const devices = await navigator.mediaDevices.enumerateDevices();
            const videoDevices = devices.filter((d) => d.kind === 'videoinput');
            setAvailableCameras(videoDevices);
            return videoDevices;
        } catch {
            return [];
        }
    };

    const startCameraWithDevice = async (deviceId?: string) => {
        try {
            const constraints: MediaStreamConstraints = {
                video: deviceId
                    ? { deviceId: { exact: deviceId }, width: { ideal: 2000 }, height: { ideal: 2000 } }
                    : { facingMode: 'environment', width: { ideal: 2000 }, height: { ideal: 2000 } },
            };

            const stream = await navigator.mediaDevices.getUserMedia(constraints);
            setCameraStream(stream);

            const activeTrack = stream.getVideoTracks()[0];
            const activeDeviceId = activeTrack?.getSettings().deviceId;
            if (activeDeviceId) {
                setSelectedCameraId(activeDeviceId);
            }

            setTimeout(() => {
                if (videoRef.current) {
                    videoRef.current.srcObject = stream;
                }
            }, 100);

            return stream;
        } catch (err) {
            console.error('Camera access error:', err);
            setCameraError('Kamera-Zugriff nicht möglich. Bitte erlaube den Kamerazugriff in den Browser-Einstellungen.');
            return null;
        }
    };

    const startCamera = async () => {
        setCameraError(null);
        setCapturedImage(null);
        setCapturedFile(null);
        setCameraDialogOpen(true);

        const stream = await startCameraWithDevice();
        if (!stream) {
            return;
        }

        const cameras = await loadAvailableCameras();

        if (cameras.length > 1) {
            const backCamera = cameras.find(isBackCamera);
            const activeTrack = stream.getVideoTracks()[0];
            const activeDeviceId = activeTrack?.getSettings().deviceId;

            if (backCamera && backCamera.deviceId !== activeDeviceId) {
                stream.getTracks().forEach((track) => track.stop());
                await startCameraWithDevice(backCamera.deviceId);
            }
        }
    };

    // Auto-open camera when autoOpen prop is true
    const hasAutoOpenedRef = useRef(false);
    useEffect(() => {
        if (autoOpen && !hasAutoOpenedRef.current && !cameraDialogOpen) {
            hasAutoOpenedRef.current = true;
            startCamera();
        }
        if (!autoOpen) {
            hasAutoOpenedRef.current = false;
        }
    }, [autoOpen]);

    const switchCamera = async (deviceId: string) => {
        if (cameraStream) {
            cameraStream.getTracks().forEach((track) => track.stop());
            setCameraStream(null);
        }
        await startCameraWithDevice(deviceId);
    };

    const stopCamera = useCallback(() => {
        if (cameraStream) {
            cameraStream.getTracks().forEach((track) => track.stop());
            setCameraStream(null);
        }
    }, [cameraStream]);

    const capturePhoto = () => {
        if (!videoRef.current || !canvasRef.current) {
            return;
        }

        const video = videoRef.current;
        const canvas = canvasRef.current;

        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;

        const ctx = canvas.getContext('2d');
        if (!ctx) {
            return;
        }

        ctx.drawImage(video, 0, 0);

        canvas.toBlob(
            (blob) => {
                if (!blob) {
                    return;
                }

                const file = new File([blob], `capture-${Date.now()}.jpg`, { type: 'image/jpeg' });

                if (autoCaptureRef.current) {
                    stopCamera();
                    onImageCaptured(file);
                    setCameraDialogOpen(false);
                    setCapturedImage(null);
                    setCapturedFile(null);
                    setCameraError(null);
                    setAvailableCameras([]);
                    setSelectedCameraId(null);
                } else {
                    setCapturedFile(file);
                    setCapturedImage(canvas.toDataURL('image/jpeg'));
                    stopCamera();
                }
            },
            'image/jpeg',
            0.9
        );
    };

    const confirmCapture = () => {
        if (capturedFile) {
            onImageCaptured(capturedFile);
            handleDialogClose();
        }
    };

    const retakePhoto = async () => {
        setCapturedImage(null);
        setCapturedFile(null);
        if (selectedCameraId) {
            await startCameraWithDevice(selectedCameraId);
        } else {
            await startCameraWithDevice();
        }
    };

    const handleDialogClose = () => {
        stopCamera();
        setCameraDialogOpen(false);
        setCapturedImage(null);
        setCapturedFile(null);
        setCameraError(null);
        setAvailableCameras([]);
        setSelectedCameraId(null);
        onCancelled?.();
    };

    return (
        <>
            <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileChange}
                accept="image/*"
                className="hidden"
            />

            {compact ? (
                <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                        <button
                            type="button"
                            disabled={disabled}
                            className="w-full h-full border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center hover:border-primary hover:bg-muted/50 transition-colors disabled:opacity-50"
                        >
                            <Plus className="h-5 w-5 text-muted-foreground" />
                        </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="start">
                        <DropdownMenuItem onClick={handleFileSelect}>
                            <FileImage className="h-4 w-4 mr-2" />
                            Datei auswählen
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={startCamera}>
                            <Camera className="h-4 w-4 mr-2" />
                            Foto aufnehmen
                        </DropdownMenuItem>
                    </DropdownMenuContent>
                </DropdownMenu>
            ) : (
                <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                        <Button variant="outline" size="sm" disabled={disabled}>
                            <Camera className="h-4 w-4 mr-2" />
                            Bild hinzufügen
                            <ChevronDown className="h-3 w-3 ml-1" />
                        </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="start">
                        <DropdownMenuItem onClick={handleFileSelect}>
                            <FileImage className="h-4 w-4 mr-2" />
                            Datei auswählen
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={startCamera}>
                            <Camera className="h-4 w-4 mr-2" />
                            Foto aufnehmen
                        </DropdownMenuItem>
                    </DropdownMenuContent>
                </DropdownMenu>
            )}

            <Dialog open={cameraDialogOpen} onOpenChange={(open) => !open && handleDialogClose()}>
                <DialogContent className="sm:max-w-2xl">
                    <DialogHeader>
                        <DialogTitle>Foto aufnehmen</DialogTitle>
                        <DialogDescription>
                            Nehmen Sie ein Foto mit Ihrer Kamera auf.
                        </DialogDescription>
                    </DialogHeader>

                    {availableCameras.length > 1 && !capturedImage && (
                        <div className="flex items-center gap-2">
                            <SwitchCamera className="h-4 w-4 text-muted-foreground shrink-0" />
                            <Select
                                value={selectedCameraId ?? undefined}
                                onValueChange={(value) => switchCamera(value)}
                            >
                                <SelectTrigger className="w-full">
                                    <SelectValue placeholder="Kamera auswählen" />
                                </SelectTrigger>
                                <SelectContent>
                                    {availableCameras.map((camera) => (
                                        <SelectItem key={camera.deviceId} value={camera.deviceId}>
                                            {camera.label || `Kamera ${availableCameras.indexOf(camera) + 1}`}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                    )}

                    <div className="relative aspect-video bg-black rounded-lg overflow-hidden">
                        {cameraError ? (
                            <div className="absolute inset-0 flex items-center justify-center p-4 text-center text-red-400">
                                {cameraError}
                            </div>
                        ) : capturedImage ? (
                            <img
                                src={capturedImage}
                                alt="Aufgenommenes Foto"
                                className="w-full h-full object-contain"
                            />
                        ) : (
                            <video
                                ref={videoRef}
                                autoPlay
                                playsInline
                                muted
                                className="w-full h-full object-contain"
                            />
                        )}
                    </div>

                    <canvas ref={canvasRef} className="hidden" />

                    <DialogFooter>
                        <Button variant="outline" onClick={handleDialogClose}>
                            <X className="h-4 w-4 mr-2" />
                            Abbrechen
                        </Button>
                        {capturedImage ? (
                            <>
                                <Button variant="outline" onClick={retakePhoto}>
                                    <Camera className="h-4 w-4 mr-2" />
                                    Neu aufnehmen
                                </Button>
                                <Button onClick={confirmCapture}>
                                    Übernehmen
                                </Button>
                            </>
                        ) : !cameraError ? (
                            <Button onClick={capturePhoto} disabled={!cameraStream}>
                                <Camera className="h-4 w-4 mr-2" />
                                Aufnehmen
                            </Button>
                        ) : null}
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    );
};

export default MedicalImageCapture;
